// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SxProps, Theme, alpha } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_DURATION_MS = 1000;
const RETRIGGER_DURATION_MS = 750;

/**
 * Reusable hook for a transient "linked field" highlight effect.
 * When a field is programmatically updated (e.g. date cascading), call
 * `trigger(fieldPath)` to activate a visual flourish on that field.
 * The flourish automatically clears after `durationMs`. If re-triggered
 * while already active, the duration extends by 750ms from now.
 */
export function useLinkedFieldFlourish(durationMs = DEFAULT_DURATION_MS) {
  const [activeFields, setActiveFields] = useState<Map<string, number>>(
    () => new Map()
  );
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const trigger = useCallback(
    (fieldPath: string) => {
      const timers = timersRef.current;
      const isRetrigger = timers.has(fieldPath);

      const existing = timers.get(fieldPath);
      if (existing !== undefined) {
        clearTimeout(existing);
      }

      setActiveFields((prev) => {
        const next = new Map(prev);
        next.set(fieldPath, (prev.get(fieldPath) ?? 0) + 1);
        return next;
      });

      const timeout = isRetrigger ? RETRIGGER_DURATION_MS : durationMs;

      const timer = setTimeout(() => {
        timers.delete(fieldPath);
        setActiveFields((prev) => {
          const next = new Map(prev);
          next.delete(fieldPath);
          return next;
        });
      }, timeout);

      timers.set(fieldPath, timer);
    },
    [durationMs]
  );

  const isActive = useCallback(
    (fieldPath: string): boolean => {
      return activeFields.has(fieldPath);
    },
    [activeFields]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
      timers.clear();
    };
  }, []);

  // Memoize the returned object so it doesn't re-allocate on every render.
  // Consumers that depend on the whole `flourish` reference in a useEffect
  // deps array won't trigger spurious effect re-runs on each render. The
  // object identity still changes when state updates (via `isActive`), so
  // callers that need fresh state still see it.
  return useMemo(() => ({ trigger, isActive }), [trigger, isActive]);
}

// Base SX applied to date pickers (and other outlined inputs) that participate
// in the flourish effect so the highlight transitions smoothly on/off.
const FLOURISH_FIELD_BASE_SX = {
  '& .MuiOutlinedInput-root': {
    transition: 'background-color 0.3s ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    transition: 'border-color 0.3s ease',
  },
} as const;

// Returns the SX for a flourish-eligible outlined input. When `isActive` is
// true the highlight (primary tint + thicker primary border) is layered on
// the base transition styles. Pair with `useLinkedFieldFlourish`'s
// `isActive(fieldPath)`.
export function flourishFieldSx(
  theme: Theme,
  isActive: boolean
): SxProps<Theme> {
  if (!isActive) {
    return FLOURISH_FIELD_BASE_SX;
  }
  return {
    ...FLOURISH_FIELD_BASE_SX,
    '& .MuiOutlinedInput-root': {
      transition: 'background-color 0.3s ease',
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      transition: 'border-color 0.3s ease',
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  };
}

// Exposes just the transition styles for callers that layer their own
// conditional highlight on top (e.g. an error variant or a project-line-item
// link-icon adornment).
export function flourishFieldBaseSx(): SxProps<Theme> {
  return FLOURISH_FIELD_BASE_SX;
}
